// src/pages/admin/ModulesAndRightsManagementSubBranch.jsx
// ------------------------------------------------------------
// ✅ FIXES YOU ASKED
// 1) Only the TABLE area scrolls (vertical + horizontal) -> fixed-height scroll container
// 2) Table header uses THEME color (from ThemeContext + getCurrentSkin)
// 3) Sticky header inside the table scroll container
// 4) Keeps your existing API + logic (branchName from URL)
// ------------------------------------------------------------

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

import HeaderBar from "../components/HeaderBar";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";

// Theme (same pattern you use in other pages)
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BASE_URL = "https://nissi-india.com";

// ------------------------ helpers ------------------------
function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const isSelect = (v) => !v || String(v) === "Select";
const toBool = (v) => v === true || v === 1 || v === "1" || v === "true";
const toBit = (v) => (v ? 1 : 0);

function getAdminFromStorage() {
  try {
    const raw = localStorage.getItem("admin");
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function canShowInventoryCheckbox(row, field) {
  const moduleDesc = String(row.fld_moduledescription || "");
  const pageName = String(row.fld_pagename || "");

  const specialFields = ["backdateEntry", "approve", "dailyProgress", "mdApprove"];

  if (specialFields.includes(field)) {
    if (pageName === "Store Issue Note") return field === "backdateEntry" || field === "approve";
    if (
      pageName === "Grn Confirmation Entry" ||
      pageName === "Bill Passing" ||
      pageName === "STN Inward/Outward" ||
      pageName === "Direct STN Outward"
    ) {
      return field === "approve";
    }
    if (pageName === "Tender Details") return field === "dailyProgress" || field === "mdApprove";
    if (pageName === "Daily Progress Entry") return field === "mdApprove";
    return false;
  }

  if (moduleDesc === "Inventory Masters") {
    if (pageName !== "Item Category") {
      if (field === "print" || field === "email" || field === "sms") return false;
    }
    return true;
  }

  if (moduleDesc === "Inventory Transactions") {
    if (field === "delete" || field === "print" || field === "email") return false;
    return true;
  }

  if (moduleDesc === "Inventory Reports" || moduleDesc === "Vehicle Reports") {
    if (field === "add" || field === "delete" || field === "sms") return false;
    return true;
  }

  return true;
}

function canShowPayrollCheckbox(row, field) {
  const subModuleName = String(row.fld_submodulename || "");
  const pageName = String(row.fld_pagename || "");
  const pageId = String(row.fld_pageid || "");

  if (field === "depVisible") return pageName === "Daily Attendance";

  if (field === "sms") {
    const forced = pageId === "35" || pageId === "37" || pageId === "40" || pageId === "80";
    if (forced) return true;
  }

  if (subModuleName === "Masters") {
    if (field === "delete" || field === "print" || field === "email") return false;
    return true;
  }
  if (subModuleName === "Transactions") {
    if (field === "add" || field === "delete" || field === "print" || field === "email") return false;
    return true;
  }
  if (subModuleName === "Reports") {
    if (field === "add" || field === "delete" || field === "sms") return false;
    return true;
  }

  return true;
}

function CellCheckbox({ checked, onChange, disabled, hidden }) {
  if (hidden) return <span className="text-slate-300">—</span>;
  return (
    <input
      type="checkbox"
      className="h-4 w-4 accent-current" // accent uses current text color (so we can theme)
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      disabled={!!disabled}
    />
  );
}

export default function ModulesAndRightsManagementSubBranch() {
  const location = useLocation();
  const admin = useMemo(() => getAdminFromStorage(), []);
  const adminId = admin?.id || 0;

  // ✅ Theme
  const { skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin || { baseColor: "slate", baseShade: 800 };

  // Tailwind dynamic classes (you already use this pattern elsewhere)
  const headerBgClass = `bg-${baseColor}-${baseShade}`;
  const headerTextClass = "text-white";
  const headerBorderClass = "border-white/20";

  // ✅ BranchName from URL (ASPX Session["Branchnm"])
  const branchNameFromUrl = useMemo(() => {
    const v = new URLSearchParams(location.search).get("branchName");
    return v ? String(v) : "";
  }, [location.search]);

  // fallback only if URL missing
  const branchName = branchNameFromUrl || admin?.username || "";

  // UI state
  const [mode, setMode] = useState("userwise");
  const [overwriteAll, setOverwriteAll] = useState(false);

  const [usertypes, setUsertypes] = useState([]);
  const [userTypeId, setUserTypeId] = useState("Select");

  const [users, setUsers] = useState([]);
  const [setupId, setSetupId] = useState("Select");

  const [modules, setModules] = useState([]);
  const [moduleName, setModuleName] = useState("Select");

  const [submodules, setSubmodules] = useState([]);
  const [subModuleId, setSubModuleId] = useState("Select");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const isPayroll = String(moduleName || "").toLowerCase() === "payroll";

  // ------------------------ API calls ------------------------
  async function loadModules() {
    const r = await axios.get(`${BASE_URL}/api/rights-subbranch/meta/modules`, {
      headers: tenantHeaders(),
      params: tenantParams({ branchName }),
    });
    setModules(r.data?.modules || []);
  }
  async function loadUsertypes(currentModuleName) {
    const r = await axios.get(`${BASE_URL}/api/rights-subbranch/meta/usertypes`, {
      headers: tenantHeaders(),
      params: tenantParams({ moduleName: currentModuleName || "" }),
    });
    const types = r.data?.usertypes || [];
    setUsertypes(types);

    if (types.length) {
      const first = String(types[0].fld_usertypeid);
      setUserTypeId(first);
      return first;
    }
    setUserTypeId("Select");
    return "Select";
  }

  async function loadUsers(currentModuleName, userTypeIdLocal) {
    const r = await axios.get(`${BASE_URL}/api/rights-subbranch/meta/users`, {
      headers: tenantHeaders(),
      params: tenantParams({
        moduleName: currentModuleName || "",
        userTypeId: userTypeIdLocal,
        branchUserName: branchName,
      }),
    });

    const list = r.data?.users || [];
    setUsers(list);
    if (list.length) setSetupId(String(list[0].fld_setupid));
    else setSetupId("Select");
  }

  async function loadSubmodules(currentModuleName) {
    if (isSelect(currentModuleName)) {
      setSubmodules([]);
      setSubModuleId("Select");
      return;
    }
    const r = await axios.get(`${BASE_URL}/api/rights-subbranch/meta/submodules`, {
      headers: tenantHeaders(),
      params: tenantParams({ moduleName: currentModuleName }),
    });
    setSubmodules(r.data?.submodules || []);
    setSubModuleId("Select");
  }

  useEffect(() => {
    if (!branchName) {
      toast.error("BranchName not found. Open with ?branchName=...");
      return;
    }
    (async () => {
      try {
        await loadModules();
        const ut = await loadUsertypes("Select");
        if (!isSelect(ut)) await loadUsers("Select", ut);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load initial data");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchName]);

  useEffect(() => {
    (async () => {
      try {
        setRows([]);
        setSubmodules([]);
        setSubModuleId("Select");

        const ut = await loadUsertypes(moduleName);
        if (!isSelect(ut)) await loadUsers(moduleName, ut);

        await loadSubmodules(moduleName);
      } catch (e) {
        console.error(e);
        toast.error("Failed to refresh dropdowns");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName]);

  // ------------------------ Search ------------------------
  async function onSearch() {
    if (isSelect(userTypeId)) return toast.error("Please select usertype");
    if (mode === "userwise" && isSelect(setupId)) return toast.error("Please select user name");

    setLoading(true);
    try {
      const r = await axios.post(
        `${BASE_URL}/api/rights-subbranch/search`,
        {
          moduleName,
          subModuleId,
          setupId: mode === "userwise" ? setupId : setupId,
        },
        { headers: tenantHeaders() }
      );

      const list = r.data?.rows || [];
      const normalized = list.map((x) => ({
        fld_pageid: x.fld_pageid,
        fld_pagename: x.fld_pagename,
        fld_url: x.fld_url,
        fld_moduledescription: x.fld_moduledescription,
        fld_submodulename: x.fld_submodulename,

        fld_rights: toBool(x.fld_rights),
        fld_addrights: toBool(x.fld_addrights),
        fld_editrights: toBool(x.fld_editrights),
        fld_deleterights: toBool(x.fld_deleterights),
        fld_printrights: toBool(x.fld_printrights),
        fld_emailrights: toBool(x.fld_emailrights),
        fld_smsrights: toBool(x.fld_smsrights),

        fld_camprights: toBool(x.fld_camprights),
        fld_backdateentryrights: toBool(x.fld_backdateentryrights),
        fld_approverights: toBool(x.fld_approverights),
        fld_mdapproverights: toBool(x.fld_mdapproverights),
        fld_dailyprogressrights: toBool(x.fld_dailyprogressrights),

        fld_DepVisiblerights: toBool(x.fld_DepVisiblerights),
      }));

      setRows(normalized);
    } catch (e) {
      console.error(e);
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  }

  // ------------------------ Header select-all ------------------------
  function selectAll(field, checked) {
    setRows((prev) =>
      prev.map((r) => {
        const visible = isPayroll
          ? canShowPayrollCheckbox(r, field)
          : canShowInventoryCheckbox(r, field);
        if (!visible) return r;

        const next = { ...r };
        switch (field) {
          case "rights":
            next.fld_rights = checked;
            break;
          case "add":
            next.fld_addrights = checked;
            if (isPayroll) next.fld_editrights = checked;
            break;
          case "edit":
            next.fld_editrights = checked;
            break;
          case "delete":
            next.fld_deleterights = checked;
            break;
          case "print":
            next.fld_printrights = checked;
            break;
          case "email":
            next.fld_emailrights = checked;
            break;
          case "sms":
            next.fld_smsrights = checked;
            break;
          case "camp":
            next.fld_camprights = checked;
            break;
          case "backdateEntry":
            next.fld_backdateentryrights = checked;
            break;
          case "approve":
            next.fld_approverights = checked;
            break;
          case "dailyProgress":
            next.fld_dailyprogressrights = checked;
            break;
          case "mdApprove":
            next.fld_mdapproverights = checked;
            break;
          case "depVisible":
            next.fld_DepVisiblerights = checked;
            break;
          default:
            break;
        }
        return next;
      })
    );
  }

  // ------------------------ Save (SET/RESET) ------------------------
  async function onSave() {
    if (isSelect(userTypeId)) return toast.error("Please select usertype");
    if (mode === "userwise" && isSelect(setupId)) return toast.error("Please select user name");
    if (rows.length === 0) return toast.error("No rows to save");

    const payloadRows = rows.map((r) => ({
      pageId: Number(r.fld_pageid),

      rights: toBit(r.fld_rights),
      add: toBit(r.fld_addrights),
      edit: toBit(r.fld_editrights),
      delete: toBit(r.fld_deleterights),
      print: toBit(r.fld_printrights),
      email: toBit(r.fld_emailrights),
      sms: toBit(r.fld_smsrights),

      camp: toBit(r.fld_camprights),
      backdateEntry: toBit(r.fld_backdateentryrights),
      approve: toBit(r.fld_approverights),
      dailyProgress: toBit(r.fld_dailyprogressrights),
      mdApprove: toBit(r.fld_mdapproverights),

      depVisible: toBit(r.fld_DepVisiblerights),
    }));

    setLoading(true);
    try {
      await axios.post(
        `${BASE_URL}/api/rights-subbranch/save`,
        {
          mode,
          overwriteAll,
          moduleName,
          subModuleId,
          setupId: mode === "userwise" ? Number(setupId) : undefined,
          userTypeId: mode === "usertype" ? Number(userTypeId) : undefined,
          branchUserName: branchName,
          rows: payloadRows,
          adminId,
        },
        { headers: tenantHeaders() }
      );

      toast.success("Saved successfully");
    } catch (e) {
      console.error(e);
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  }

  function updateRow(idx, patch) {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }

  // ✅ table column count for empty state
  const colSpan = isPayroll ? 11 : 15;

  return (
    <AdminLayoutWrapper>
      <HeaderBar />
      <div className="p-4">
        <ToastContainer />

        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold">Module and Rights Management (Sub Branch)</h1>
          <button
            onClick={onSave}
            disabled={loading || rows.length === 0}
            className={`rounded px-4 py-2 text-white disabled:opacity-50 ${headerBgClass}`}
          >
            {loading ? "Working..." : "Set / Reset"}
          </button>
        </div>

        {/* Top controls */}
        <div className="rounded border bg-white p-4">
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="mode"
                checked={mode === "usertype"}
                onChange={() => setMode("usertype")}
              />
              <span>User Type wise</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={overwriteAll}
                onChange={(e) => setOverwriteAll(e.target.checked)}
                disabled={mode !== "usertype"}
              />
              <span>Overwrite rights to all users</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="mode"
                checked={mode === "userwise"}
                onChange={() => setMode("userwise")}
              />
              <span>User wise</span>
            </label>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">
                User Type <span className="text-red-500">*</span>
              </label>
              <select className="w-full rounded border px-3 py-2" value={userTypeId} disabled>
                {usertypes.map((u) => (
                  <option key={u.fld_usertypeid} value={String(u.fld_usertypeid)}>
                    {u.fld_usertype}
                  </option>
                ))}
                {!usertypes.length && <option value="Select">Select</option>}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">User Name</label>
              <select
                className="w-full rounded border px-3 py-2"
                value={setupId}
                disabled={mode !== "userwise"}
                onChange={(e) => setSetupId(e.target.value)}
              >
                <option value="Select">Select</option>
                {users.map((u) => (
                  <option key={u.fld_setupid} value={String(u.fld_setupid)}>
                    {u.fld_username}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">Module Name</label>
              <select
                className="w-full rounded border px-3 py-2"
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
              >
                <option value="Select">Select</option>
                {modules.map((m) => (
                  <option key={m.fld_moduleid} value={String(m.fld_modulename)}>
                    {m.fld_modulename}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium">SubModule Name</label>
              <select
                className="w-full rounded border px-3 py-2"
                value={subModuleId}
                onChange={(e) => setSubModuleId(e.target.value)}
                disabled={isSelect(moduleName)}
              >
                <option value="Select">Select</option>
                {submodules.map((sm) => (
                  <option key={sm.fld_moduleid} value={String(sm.fld_moduleid)}>
                    {sm.fld_moduledescription}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={onSearch}
              disabled={loading}
              className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>

        {/* ✅ TABLE BLOCK (ONLY this scrolls) */}
        <div className="mt-4 rounded border bg-white">
          {/* ✅ fixed height + scroll only here */}
          <div className="h-[420px] overflow-auto">
            <table className="min-w-[1400px] w-full border-collapse">
              <thead className="sticky top-0 z-10">
                {/* ✅ theme header */}
                <tr className={`${headerBgClass} ${headerTextClass} text-sm`}>
                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>Slno</th>
                  <th className={`border px-2 py-2 ${headerBorderClass}`}>Module Name</th>
                  <th className={`border px-2 py-2 ${headerBorderClass}`}>Sub Module Name</th>
                  <th className={`border px-2 py-2 ${headerBorderClass}`}>Page Name</th>

                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-white"
                        onChange={(e) => selectAll("rights", e.target.checked)}
                      />
                      <span>Rights</span>
                    </div>
                  </th>

                  {isPayroll ? (
                    <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-white"
                          onChange={(e) => selectAll("add", e.target.checked)}
                        />
                        <span>Add/Edit</span>
                      </div>
                    </th>
                  ) : (
                    <>
                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("add", e.target.checked)}
                          />
                          <span>Add</span>
                        </div>
                      </th>

                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("edit", e.target.checked)}
                          />
                          <span>Edit</span>
                        </div>
                      </th>
                    </>
                  )}

                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-white"
                        onChange={(e) => selectAll("delete", e.target.checked)}
                      />
                      <span>Delete</span>
                    </div>
                  </th>

                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-white"
                        onChange={(e) => selectAll("print", e.target.checked)}
                      />
                      <span>Print</span>
                    </div>
                  </th>

                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-white"
                        onChange={(e) => selectAll("email", e.target.checked)}
                      />
                      <span>Email</span>
                    </div>
                  </th>

                  <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-white"
                        onChange={(e) => selectAll("sms", e.target.checked)}
                      />
                      <span>SMS</span>
                    </div>
                  </th>

                  {!isPayroll && (
                    <>
                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("camp", e.target.checked)}
                          />
                          <span>Camp</span>
                        </div>
                      </th>

                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("backdateEntry", e.target.checked)}
                          />
                          <span>Allow Backdate Entry</span>
                        </div>
                      </th>

                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("approve", e.target.checked)}
                          />
                          <span>Approve</span>
                        </div>
                      </th>

                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("dailyProgress", e.target.checked)}
                          />
                          <span>DailyProgress</span>
                        </div>
                      </th>

                      <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                        <div className="flex items-center justify-center gap-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-white"
                            onChange={(e) => selectAll("mdApprove", e.target.checked)}
                          />
                          <span>MD Approve</span>
                        </div>
                      </th>
                    </>
                  )}

                  {isPayroll && (
                    <th className={`border px-2 py-2 text-center ${headerBorderClass}`}>
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-white"
                          onChange={(e) => selectAll("depVisible", e.target.checked)}
                        />
                        <span>Department</span>
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="text-sm text-slate-700">
                {rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-6 text-center text-slate-500" colSpan={colSpan}>
                      No Records Found
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const show = (field) =>
                      isPayroll ? canShowPayrollCheckbox(r, field) : canShowInventoryCheckbox(r, field);

                    return (
                      <tr key={`${r.fld_pageid}-${idx}`} className="hover:bg-slate-50">
                        <td className="border px-2 py-2 text-center">{idx + 1}</td>
                        <td className="border px-2 py-2">{r.fld_moduledescription}</td>
                        <td className="border px-2 py-2">{r.fld_submodulename}</td>
                        <td className="border px-2 py-2">{r.fld_pagename}</td>

                        <td className="border px-2 py-2 text-center">
                          <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                            <CellCheckbox
                              checked={r.fld_rights}
                              hidden={!show("rights")}
                              onChange={(v) => updateRow(idx, { fld_rights: v })}
                            />
                          </span>
                        </td>

                        {isPayroll ? (
                          <td className="border px-2 py-2 text-center">
                            <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                              <CellCheckbox
                                checked={r.fld_addrights}
                                hidden={!show("add")}
                                onChange={(v) => updateRow(idx, { fld_addrights: v, fld_editrights: v })}
                              />
                            </span>
                          </td>
                        ) : (
                          <>
                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_addrights}
                                  hidden={!show("add")}
                                  onChange={(v) => updateRow(idx, { fld_addrights: v })}
                                />
                              </span>
                            </td>
                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_editrights}
                                  hidden={!show("edit")}
                                  onChange={(v) => updateRow(idx, { fld_editrights: v })}
                                />
                              </span>
                            </td>
                          </>
                        )}

                        <td className="border px-2 py-2 text-center">
                          <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                            <CellCheckbox
                              checked={r.fld_deleterights}
                              hidden={!show("delete")}
                              onChange={(v) => updateRow(idx, { fld_deleterights: v })}
                            />
                          </span>
                        </td>

                        <td className="border px-2 py-2 text-center">
                          <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                            <CellCheckbox
                              checked={r.fld_printrights}
                              hidden={!show("print")}
                              onChange={(v) => updateRow(idx, { fld_printrights: v })}
                            />
                          </span>
                        </td>

                        <td className="border px-2 py-2 text-center">
                          <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                            <CellCheckbox
                              checked={r.fld_emailrights}
                              hidden={!show("email")}
                              onChange={(v) => updateRow(idx, { fld_emailrights: v })}
                            />
                          </span>
                        </td>

                        <td className="border px-2 py-2 text-center">
                          <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                            <CellCheckbox
                              checked={r.fld_smsrights}
                              hidden={!show("sms")}
                              onChange={(v) => updateRow(idx, { fld_smsrights: v })}
                            />
                          </span>
                        </td>

                        {!isPayroll && (
                          <>
                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_camprights}
                                  hidden={!show("camp")}
                                  onChange={(v) => updateRow(idx, { fld_camprights: v })}
                                />
                              </span>
                            </td>

                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_backdateentryrights}
                                  hidden={!show("backdateEntry")}
                                  onChange={(v) => updateRow(idx, { fld_backdateentryrights: v })}
                                />
                              </span>
                            </td>

                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_approverights}
                                  hidden={!show("approve")}
                                  onChange={(v) => updateRow(idx, { fld_approverights: v })}
                                />
                              </span>
                            </td>

                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_dailyprogressrights}
                                  hidden={!show("dailyProgress")}
                                  onChange={(v) => updateRow(idx, { fld_dailyprogressrights: v })}
                                />
                              </span>
                            </td>

                            <td className="border px-2 py-2 text-center">
                              <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                                <CellCheckbox
                                  checked={r.fld_mdapproverights}
                                  hidden={!show("mdApprove")}
                                  onChange={(v) => updateRow(idx, { fld_mdapproverights: v })}
                                />
                              </span>
                            </td>
                          </>
                        )}

                        {isPayroll && (
                          <td className="border px-2 py-2 text-center">
                            <span className={`inline-flex items-center justify-center text-${baseColor}-${baseShade}`}>
                              <CellCheckbox
                                checked={r.fld_DepVisiblerights}
                                hidden={!show("depVisible")}
                                onChange={(v) => updateRow(idx, { fld_DepVisiblerights: v })}
                              />
                            </span>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t p-3 text-xs text-slate-500">
            <div>
              Branch: <span className="font-semibold">{branchName || "—"}</span>
            </div>
            <div>{rows.length ? `${rows.length} page(s)` : ""}</div>
          </div>
        </div>
      </div>
    </AdminLayoutWrapper>
  );
}
